import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Alert,
  LinearProgress,
  Snackbar,
  useTheme,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import dataQualityService from '../../services/dataQualityService';

const QualityRules = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [rules, setRules] = useState([]);

  const [newRule, setNewRule] = useState({
    name: '',
    category: '',
    description: '',
    severity: 'medium',
    entityType: '',
    field: '',
    condition: '',
    enabled: true
  });

  const categories = ['Completeness', 'Validity', 'Integrity', 'Business Logic', 'Accuracy', 'Uniqueness'];
  const severityLevels = ['low', 'medium', 'high', 'critical'];
  const entityTypes = ['organization', 'facility', 'emission_statement', 'equipment'];

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dataQualityService.getRules();
      const ruleList = (data?.rules ?? data ?? []).map(r => ({
        id: r.id || r.rule_id,
        name: r.name,
        category: r.category || r.dimension_type || 'General',
        description: r.description || '',
        status: (r.status || r.status_name || 'active').toLowerCase(),
        severity: r.severity || 'medium',
        errorCount: r.errorCount || r.error_count || 0,
        impactPercentage: r.impactPercentage || r.impact_percentage || 0,
        lastRun: r.updatedAt || r.updated_at || r.createdAt || r.created_at,
        createdBy: r.createdBy || r.created_by || 'System',
        entityType: r.entityType || r.entity_type || 'organization',
        field: r.field || '',
        condition: r.ruleStatement || r.rule_statement || r.condition || '',
        enabled: (r.status || r.status_name || 'active').toLowerCase() === 'active'
      }));
      setRules(ruleList);
    } catch (err) {
      console.error('Error fetching rules:', err);
      setSnackbar({ open: true, message: `Failed to load rules: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return theme.palette.error.main;
      case 'high': return theme.palette.warning.main;
      case 'medium': return theme.palette.info.main;
      case 'low': return theme.palette.success.main;
      default: return theme.palette.grey[500];
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <ErrorIcon />;
      case 'high': return <WarningIcon />;
      case 'medium': return <WarningIcon />;
      case 'low': return <CheckCircleIcon />;
      default: return <WarningIcon />;
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' ? theme.palette.success.main : theme.palette.grey[500];
  };

  const filteredRules = rules.filter(rule => {
    const matchesSearch = rule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         rule.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || rule.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || rule.category === filterCategory;
    const matchesActive = !showActiveOnly || rule.enabled;
    return matchesSearch && matchesStatus && matchesCategory && matchesActive;
  });

  const handleMenuOpen = (event, rule) => {
    setAnchorEl(event.currentTarget);
    setSelectedRule(rule);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRule(null);
  };

  const handleDialogOpen = (type, rule = null) => {
    setDialogType(type);
    if (rule) {
      setSelectedRule(rule);
      setNewRule({
        name: rule.name,
        category: rule.category,
        description: rule.description,
        severity: rule.severity,
        entityType: rule.entityType,
        field: rule.field,
        condition: rule.condition,
        enabled: rule.enabled
      });
    } else {
      setSelectedRule(null);
      setNewRule({ name: '', category: '', description: '', severity: 'medium', entityType: '', field: '', condition: '', enabled: true });
    }
    setDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedRule(null);
    setNewRule({ name: '', category: '', description: '', severity: 'medium', entityType: '', field: '', condition: '', enabled: true });
  };

  const handleSaveRule = async () => {
    try {
      if (dialogType === 'create') {
        await dataQualityService.createRule({
          name: newRule.name,
          description: newRule.description,
          rule_statement: newRule.condition,
          dimension_type: newRule.category,
          status: newRule.enabled ? 'Active' : 'Draft'
        });
        setSnackbar({ open: true, message: 'Rule created successfully', severity: 'success' });
      } else if (dialogType === 'edit' && selectedRule) {
        await dataQualityService.updateRule(selectedRule.id, {
          name: newRule.name,
          description: newRule.description,
          rule_statement: newRule.condition,
          dimension_type: newRule.category,
          status: newRule.enabled ? 'Active' : 'Draft'
        });
        setSnackbar({ open: true, message: 'Rule updated successfully', severity: 'success' });
      }
      handleDialogClose();
      fetchRules();
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to save rule: ${err.message}`, severity: 'error' });
    }
  };

  const handleDeleteRule = async () => {
    if (selectedRule) {
      try {
        await dataQualityService.deleteRule(selectedRule.id);
        setSnackbar({ open: true, message: 'Rule deleted successfully', severity: 'success' });
        fetchRules();
      } catch (err) {
        setSnackbar({ open: true, message: `Failed to delete rule: ${err.message}`, severity: 'error' });
      }
    }
    handleMenuClose();
  };

  const renderRuleCard = (rule) => (
    <Card key={rule.id} sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">{rule.name}</Typography>
              <Chip label={rule.category} size="small" sx={{ ml: 1, mr: 1 }} color="primary" variant="outlined" />
              <Chip
                label={rule.severity}
                size="small"
                sx={{ color: getSeverityColor(rule.severity), borderColor: getSeverityColor(rule.severity) }}
                variant="outlined"
                icon={getSeverityIcon(rule.severity)}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{rule.description}</Typography>
            {rule.condition && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontFamily: 'monospace' }}>
                {rule.condition}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2"><strong>Entity:</strong> {rule.entityType}</Typography>
              <Typography variant="body2"><strong>Created by:</strong> {rule.createdBy}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={rule.status} size="small" sx={{ color: getStatusColor(rule.status), borderColor: getStatusColor(rule.status) }} variant="outlined" />
            <IconButton onClick={(e) => handleMenuOpen(e, rule)} size="small"><MoreVertIcon /></IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderRuleTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Rule Name</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Severity</TableCell>
            <TableCell>Entity Type</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredRules
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((rule) => (
              <TableRow key={rule.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{rule.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{rule.description}</Typography>
                </TableCell>
                <TableCell><Chip label={rule.category} size="small" color="primary" variant="outlined" /></TableCell>
                <TableCell>
                  <Chip label={rule.severity} size="small" sx={{ color: getSeverityColor(rule.severity), borderColor: getSeverityColor(rule.severity) }} variant="outlined" />
                </TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>{rule.entityType}</TableCell>
                <TableCell>
                  <Chip label={rule.status} size="small" sx={{ color: getStatusColor(rule.status), borderColor: getStatusColor(rule.status) }} variant="outlined" />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleDialogOpen('view', rule)}><VisibilityIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Rule">
                      <IconButton size="small" onClick={() => handleDialogOpen('edit', rule)}><EditIcon /></IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, rule)}><MoreVertIcon /></IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredRules.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />
    </TableContainer>
  );

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>Loading quality rules...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Quality Rules</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleDialogOpen('create')}>Add Rule</Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search rules..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Status">
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="deprecated">Deprecated</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Category</InputLabel>
          <Select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} label="Category">
            <MenuItem value="all">All Categories</MenuItem>
            {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={showActiveOnly} onChange={(e) => setShowActiveOnly(e.target.checked)} size="small" />}
          label="Active Only"
        />
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Card View" />
          <Tab label="Table View" />
        </Tabs>
      </Box>

      {tabValue === 0 ? (
        <Box>{filteredRules.map(rule => renderRuleCard(rule))}</Box>
      ) : (
        renderRuleTable()
      )}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleDialogOpen('view', selectedRule)}><VisibilityIcon sx={{ mr: 1 }} />View Details</MenuItem>
        <MenuItem onClick={() => handleDialogOpen('edit', selectedRule)}><EditIcon sx={{ mr: 1 }} />Edit Rule</MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteRule} sx={{ color: 'error.main' }}><DeleteIcon sx={{ mr: 1 }} />Delete Rule</MenuItem>
      </Menu>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogType === 'create' ? 'Create New Rule' : dialogType === 'edit' ? 'Edit Rule' : 'Rule Details'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Rule Name" value={newRule.name} onChange={(e) => setNewRule({...newRule, name: e.target.value})} disabled={dialogType === 'view'} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select value={newRule.category} onChange={(e) => setNewRule({...newRule, category: e.target.value})} label="Category" disabled={dialogType === 'view'}>
                    {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Description" value={newRule.description} onChange={(e) => setNewRule({...newRule, description: e.target.value})} multiline rows={3} disabled={dialogType === 'view'} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Severity</InputLabel>
                  <Select value={newRule.severity} onChange={(e) => setNewRule({...newRule, severity: e.target.value})} label="Severity" disabled={dialogType === 'view'}>
                    {severityLevels.map(l => <MenuItem key={l} value={l} sx={{ textTransform: 'capitalize' }}>{l}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Entity Type</InputLabel>
                  <Select value={newRule.entityType} onChange={(e) => setNewRule({...newRule, entityType: e.target.value})} label="Entity Type" disabled={dialogType === 'view'}>
                    {entityTypes.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch checked={newRule.enabled} onChange={(e) => setNewRule({...newRule, enabled: e.target.checked})} disabled={dialogType === 'view'} />}
                  label="Enabled"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Rule Statement / Condition" value={newRule.condition} onChange={(e) => setNewRule({...newRule, condition: e.target.value})} multiline rows={3} disabled={dialogType === 'view'} helperText="SQL-like condition expression" />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          {dialogType !== 'view' && (
            <Button onClick={handleSaveRule} variant="contained">{dialogType === 'create' ? 'Create' : 'Update'}</Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default QualityRules;
